<template>
	<div class="container position-relative">
		<div class="grid-md gutters-300-md">
			<div class="col-7-md">
				<div
					v-if="isAccreditedOnly"
					class="overline-small text-color-content-tertiary mb-50"
					data-test="accredited-only"
					>Accredited Only</div
				>
				<h1 class="heading-1">{{ fundName }}</h1>
				<div class="display-flex align-items-center mt-100 mb-150">
					<StatusTag
						v-if="showFundStatus"
						:text="fundStatus"
						:background="fundStatusBackground"
						data-test="status-tag"
					/>
					<Tag :text="fundType" />
				</div>
				<div v-if="appStore.isMobile && isNotFlagship2" data-test="mobile-cta">
					<div v-if="appStore.isAuthenticated" class="my-150">
						<p v-if="hasMinimumInvestment" class="body-sm mb-50">{{ minimumInvestmentText }}</p>
						<DirectInvestCTA
							v-if="
								fund.directInvestmentDetails?.directInvestmentEligibility &&
								fund.directInvestmentDetails?.reitOfferingVintageId
							"
							:display-unavailable-state="displayUnavailableDirectInvestState"
							:direct-investment-eligibility="fund.directInvestmentDetails.directInvestmentEligibility"
							:offering-vintage-id="fund.directInvestmentDetails.reitOfferingVintageId"
							trigger="fund-detail"
							classes="button-block body-lg"
						/>
						<div
							v-if="showReservationText"
							class="mt-100 p-100 bg-color-greige"
							v-html="windowReservationDisplayText"
						></div>
					</div>
					<div v-else class="my-150" name="unauthenticated-button-mobile">
						<slot name="unauthenticated-button" />
					</div>
				</div>
				<p>{{ fundSummary }}</p>
				<FundKeyStats
					v-if="showFundStats"
					:statistics="heroStats"
					class="mt-200-md mt-150 mb-200-smo"
					data-test="fund-key-stats"
				/>
			</div>
			<div class="col-5-md">
				<OverviewTable v-if="showFundStats" :fund="props.fund" data-test="overview-table" />
				<div v-if="!appStore.isMobile && isNotFlagship2" data-test="desktop-cta">
					<div v-if="appStore.isAuthenticated" class="mt-150">
						<p v-if="hasMinimumInvestment" class="body-sm mb-50">{{ minimumInvestmentText }}</p>
						<DirectInvestCTA
							v-if="
								fund.directInvestmentDetails?.directInvestmentEligibility &&
								fund.directInvestmentDetails?.reitOfferingVintageId
							"
							:display-unavailable-state="displayUnavailableDirectInvestState"
							:direct-investment-eligibility="fund.directInvestmentDetails.directInvestmentEligibility"
							:offering-vintage-id="fund.directInvestmentDetails.reitOfferingVintageId"
							classes="button-block body-lg"
							trigger="fund-detail"
						/>
						<div
							v-if="showReservationText"
							class="mt-100 p-100 bg-color-greige"
							v-html="windowReservationDisplayText"
						></div>
					</div>
					<div v-else class="mt-150" name="unauthenticated-button">
						<slot name="unauthenticated-button" />
					</div>
				</div>
			</div>
		</div>
		<RichTextComponent
			v-if="heroDisclaimer"
			:blocks="heroDisclaimer"
			class="col-7-md disclaimer mt-200"
			data-test="fund-details-hero-disclaimer"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import FundKeyStats from '@components/offering-details/fund-key-stats.vue';
import OverviewTable from '@components/offering-details/overview-table.vue';
import DirectInvestCTA from '@components/offerings/direct-invest-cta.vue';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import StatusTag from '@components/tags/status-tag.vue';
import Tag from '@components/tags/tag-component.vue';
import { flagship2FundOfferingId } from '@constants/reit';
import { windowReservationDescription } from '@constants/window-reservation';
import { currency, deorphanText } from '@filters/shared-filters';
import { useAppStore } from '@stores/app';
import { buildFundKeyStats } from '@utils/offerings';
import { Fund } from 'types/funds';
import { TagBackground } from 'types/layout';
import { FundKeyStatistic } from 'types/reit-details';
import { SanityRichTextBlock } from 'types/sanity';

// props
interface Props {
	showFundStatus?: boolean;
	showFundStats?: boolean;
	displayUnavailableDirectInvestState?: boolean;
	fund: Fund;
}

const props = withDefaults(defineProps<Props>(), {
	showFundStatus: true,
	showFundStats: true,
	displayUnavailableDirectInvestState: true
});

const appStore = useAppStore();

// computed
const isNotFlagship2 = computed(() => props.fund.fundriseId !== flagship2FundOfferingId);

const hasMinimumInvestment = computed((): boolean => !!props.fund.directInvestmentDetails?.minimumInvestment);

const isAccreditedOnly = computed((): boolean => props.fund.directInvestmentDetails?.accreditedOnly ?? false);

const showReservationText = computed(
	(): boolean => props.fund.directInvestmentDetails?.directInvestmentEligibility === 'RESERVATION_ELIGIBLE'
);

const heroStats = computed((): Array<FundKeyStatistic> => buildFundKeyStats(props.fund));

const heroDisclaimer = computed(
	(): Array<SanityRichTextBlock> | null => props.fund.fundDetails?.heroDisclaimer ?? null
);

const minimumInvestmentText = computed(
	(): string =>
		`Initial investment minimum of ${currency(props.fund.directInvestmentDetails?.minimumInvestment ?? '', 0)}`
);

const fundName = computed((): string => props.fund.name);

const fundStatus = computed((): string => props.fund.offeringAvailabilityStatus?.label ?? '');

const fundSummary = computed((): string => props.fund.fundSummary || '');

const fundType = computed((): string => props.fund.assetClass.label);

const fundStatusBackground = computed((): TagBackground => {
	switch (props.fund.offeringAvailabilityStatus?.name) {
		case 'OPEN':
			return 'green';
		case 'COMING_SOON':
		case 'RESERVATION_AVAILABLE':
			return 'blue';
		case 'CLOSED':
		default:
			return 'default';
	}
});

const windowReservationDisplayText = computed((): string =>
	deorphanText(windowReservationDescription(props.fund.reservationWindow?.reservationWindow.name ?? ''))
);
</script>
<style lang="scss" scoped>
@use '../../../styles/constants/colors' as *;

.divider-circle {
	&::before {
		content: '\25CF';
		margin: 0 0.5rem;
		color: #d9d9d9;
	}
}
</style>
